/* eslint-disable camelcase */
// --- Request Processors

import { globalState, parseJSON } from '@/components/helpers/utils'
import { getProductPrices } from '../../storefront/products'

/**
 *
 * @param {Facet[]} facets
 * @returns {NormalisedFacet[]}
 */
export function normaliseFacets(facets) {
  return facets.map((facet) => {
    return {
      ...facet,
      values: facet.values.filter((item) => item.value !== '"')
    }
  })
}

/**
 *
 * Normalise the results from the searchspring API
 * @param {Sorting[]} options
 * @returns {NormalisedSorting[]}
 */
export function normaliseSortOptions(options) {
  return options.map((option) => {
    return {
      ...option,
      active: option.active ?? 0
    }
  })
}

/**
 *
 * Normalise the results from the searchspring API
 * @param {Result} result
 * @returns {Swatch[]}
 */
export function normaliseSwatches(result) {
  const parsedSwatches = parseJSON(result?.mfield_dotapparel21_siblings, 'array')
  if (!parsedSwatches) return []
  const publishedSwatches = parsedSwatches.filter((swatch) => swatch.published)

  const swatchesWithImages = publishedSwatches.map((swatch) => {
    return {
      ...swatch,
      url: result.url
    }
  })
  return swatchesWithImages.sort((swatchA, swatchB) => {
    if (swatchA.isCurrentProduct === swatchB.isCurrentProduct) return 0
    if (swatchA.isCurrentProduct) return -1
    return 1
  })
}

/**
 * Normalise delimited string/array types into arrays
 * @param {String|Array} result
 * @returns {Array}
 */
export function normalisedArray(result) {
  if (result?.includes('|')) {
    return result?.split('|')
  } else if (typeof result === 'string') {
    return [result]
  } else {
    return result
  }
}

/**
 *
 * Normalise the results from the searchspring API
 * @param {Result} result
 * @returns {NormalisedImage[]}
 */
export function normaliseImages(result) {
  // Image handling
  const { images, images_alt } = result
  const srcs = normalisedArray(images)
  const alts = normalisedArray(images_alt)

  return srcs?.map((src, index) => {
    return {
      src,
      alt: alts ? alts[index] : ''
    }
  })
}

/**
 * Apply B2B pricing to search results
 * @param {NormalisedResult[]} results
 * @returns {NormalisedResult[]}
 */
function applyB2BPricing(results) {
  // Check if customer is B2B and B2B pricing is available
  const isB2BCustomer = globalState?.customer?.b2b
  const b2bPricing = globalState?.customer?.b2bPricing

  if (!isB2BCustomer || !b2bPricing) {
    return results
  }

  return results.map((result) => {
    const productId = result.uid || result.id?.toString?.() || result.id
    const b2bData = b2bPricing[productId]

    if (b2bData && b2bData.price) {
      const updatedVariants = result.variants.map((variant) => ({
        ...variant,
        price: b2bData.price.toString(),
        compare_at_price: variant.price || result.price
      }))

      const updatedResult = {
        ...result,
        price: b2bData.price.toString(),
        variants: updatedVariants
      }

      return updatedResult
    }

    return result
  })
}

/**
 *
 * Normalise the results from the searchspring API
 * @param {Result[]} results
 * @returns {Promise[NormalisedResult[]]}
 */
export async function normaliseResults(results) {
  if (!results) return []
  const normalisedResults = results?.map((result) => {
    const swatches = normaliseSwatches(result)
    const images = normaliseImages(result)
    const variants = parseJSON(result.variants, 'array')
    return {
      ...result,
      images,
      swatches,
      variants
    }
  })

  if (!normalisedResults || !normalisedResults.length) return []

  const b2bResults = applyB2BPricing(normalisedResults)

  if (globalState.localization.country !== globalState.shopify.currency) {
    return await localiseResults(b2bResults)
  }
  return b2bResults
}

async function localiseResults(normalisedResults) {
  const localisedPrices = await getProductPrices(normalisedResults)
  const localisedResults = normalisedResults.map((result) => {
    const priceMatch = localisedPrices?.find((product) => {
      return product.id.split('/').pop() === result.uid
    })

    if (priceMatch) {
      const resultVariants = result.variants.map((variant) => {
        return {
          ...variant,
          price: priceMatch.price
        }
      })
      return {
        ...result,
        variants: resultVariants,
        price: priceMatch.price
      }
    } else {
      return result
    }
  })
  return localisedResults
}

/**
 *
 * @param {Result[]} results
 * @returns {NormalisedResult[]}
 */
export async function normaliseRecommendations(results) {
  const normalisedResults = results?.results?.map((result) => {
    const swatches = normaliseSwatches(result.attributes)
    const variants = parseJSON(result.attributes.variants, 'array')
    const images = normaliseImages(result.attributes)

    return {
      ...result,
      ...result.attributes,
      ...result.mappings.core,
      images,
      variants,
      swatches
    }
  })

  if (!normalisedResults || !normalisedResults.length) return []

  const b2bResults = applyB2BPricing(normalisedResults)

  if (globalState.localization.country !== globalState.shopify.currency) {
    return await localiseResults(b2bResults)
  }

  return b2bResults
}
